import "./users.css";
import { useEffect, useState } from "react";
import {
  Board,
  BoardBody,
  BoardHead,
  BoardSortedDirectionProps,
  BoardSortedDirectionType,
  BoardTd,
  BoardTh,
  BoardTr,
  Edit,
  getSortedBoard,
  IconButton,
  Modal,
  OperationArea,
  Panel,
  Toggle,
} from "../../../components";
import { PencilOutline, TrashOutline } from "../../../assets";
import { useApiFetch, useAuth, useLanguage } from "../../../hooks";
import { API_URL, AdminRoleKey, AdminUserProps } from "../../../services";
import { PasswordStrengthMessages, validatePasswordStrength } from "../../../utils";
import EanLinksEditor, { EanLinkRow } from "./EanLinksEditor";

const isAdminRoleKey = (value: string): value is AdminRoleKey =>
  value === "admin" || value === "brand" || value === "channel";

const roleLabels = (
  roles: string[],
  t: ReturnType<typeof useLanguage>["t"]
): string =>
  roles
    .filter((role) => role !== "ROLE_USER")
    .map((role) => role.replace("ROLE_", "").toLowerCase())
    .filter(isAdminRoleKey)
    .map((key) => t.adminUsersScreen.roles[key])
    .join(", ");

const emptyForm = {
  name: "",
  eanLinks: [] as EanLinkRow[],
  apiUsers: "",
  isAdmin: false,
};

// Filas con EAN para enviar al back (las vacías se descartan).
const toEanLinksPayload = (rows: EanLinkRow[]) =>
  rows
    .filter((row) => row.ean.trim().length > 0)
    .map((row) => ({ ean: row.ean.trim(), category: row.category.trim() }));

const UsersScreen: React.FC = () => {
  const { t } = useLanguage();
  const { user: currentUserEmail } = useAuth();

  const [fetchUsers, users, loading] = useApiFetch<AdminUserProps[]>([]);
  const [fetchUpdate, , saving, updateError] = useApiFetch<AdminUserProps>();
  const [fetchCreate, , creating, createError] = useApiFetch<AdminUserProps>();
  const [fetchDelete, , deleting, deleteError] = useApiFetch<null>();

  const [editing, setEditing] = useState<AdminUserProps | null>(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editPassword, setEditPassword] = useState<string>("");
  const [editPasswordConfirm, setEditPasswordConfirm] = useState<string>("");
  const [editPasswordTouched, setEditPasswordTouched] = useState<boolean>(false);

  const [creatingOpen, setCreatingOpen] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState<string>("");
  const [newPasswordTouched, setNewPasswordTouched] = useState<boolean>(false);
  const [createForm, setCreateForm] = useState(emptyForm);

  const [deletingUser, setDeletingUser] = useState<AdminUserProps | null>(
    null
  );

  const [sortConfig, setSortConfig] = useState<BoardSortedDirectionProps>({
    key: "",
    direction: "",
  });

  const sortData = (key: string) => {
    let direction: BoardSortedDirectionType = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    if (sortConfig.key === key && sortConfig.direction === "desc") {
      setSortConfig({ key: "", direction: "" });
      return;
    }

    setSortConfig({ key, direction });
  };

  const load = async () => {
    await fetchUsers(API_URL.ADMIN_USERS(), { method: "GET" });
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (user: AdminUserProps) => {
    setEditing(user);
    setEditForm({
      name: user.name ?? "",
      eanLinks: user.eanLinks.map((link) => ({
        ean: link.ean,
        category: link.category ?? "",
        productName: link.productName,
      })),
      apiUsers: user.apiUsers.join(", "),
      isAdmin: user.isAdmin,
    });
    setEditPassword("");
    setEditPasswordConfirm("");
    setEditPasswordTouched(false);
  };

  const openCreate = () => {
    setNewEmail("");
    setNewPassword("");
    setNewPasswordConfirm("");
    setNewPasswordTouched(false);
    setCreateForm(emptyForm);
    setCreatingOpen(true);
  };

  const toList = (value: string): string[] =>
    value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

  const handleSave = async () => {
    if (!editing) {
      return;
    }

    const result = await fetchUpdate(API_URL.ADMIN_USERS(`/${editing.id}`), {
      method: "PUT",
      body: JSON.stringify({
        name: editForm.name,
        isAdmin: editForm.isAdmin,
        eanLinks: toEanLinksPayload(editForm.eanLinks),
        apiUsers: toList(editForm.apiUsers),
        ...(editPassword.trim().length > 0 ? { password: editPassword } : {}),
      }),
    });

    if (!result?.data) {
      return;
    }

    setEditing(null);
    setEditPassword("");
    setEditPasswordConfirm("");
    setEditPasswordTouched(false);
    await load();
  };

  const handleCreate = async () => {
    const result = await fetchCreate(API_URL.ADMIN_USERS(), {
      method: "POST",
      body: JSON.stringify({
        email: newEmail,
        password: newPassword,
        name: createForm.name,
        isAdmin: createForm.isAdmin,
        eanLinks: toEanLinksPayload(createForm.eanLinks),
        apiUsers: toList(createForm.apiUsers),
      }),
    });

    if (!result?.data) {
      return;
    }

    setCreatingOpen(false);
    setNewEmail("");
    setNewPassword("");
    setNewPasswordConfirm("");
    setNewPasswordTouched(false);
    setCreateForm(emptyForm);
    await load();
  };

  const handleDelete = async () => {
    if (!deletingUser) {
      return;
    }

    const result = await fetchDelete(API_URL.ADMIN_USERS(`/${deletingUser.id}`), {
      method: "DELETE",
    });

    if (result?.code && result.code >= 400) {
      return;
    }

    setDeletingUser(null);
    await load();
  };

  const passwordMessages: PasswordStrengthMessages = {
    minLengthPrefix: t.global.password.errors.minLength.prefix,
    minLengthSuffix: t.global.password.errors.minLength.suffix,
    minUppercase: t.global.password.errors.minAUppercase,
    minLowercase: t.global.password.errors.minALowercase,
    minNumber: t.global.password.errors.minANumber,
    minSpecial: t.global.password.errors.minASpecial,
  };

  const newPasswordStrengthError = validatePasswordStrength(
    newPassword,
    passwordMessages
  );
  const newPasswordMismatch = newPassword !== newPasswordConfirm;
  const isCreateDisabled =
    newEmail.trim().length === 0 ||
    newPasswordStrengthError.length > 0 ||
    newPasswordMismatch ||
    creating;

  // La contraseña es opcional al editar (vacía = no cambiarla), así que la
  // validación de fuerza solo aplica si el admin ha empezado a escribir una.
  const editPasswordStrengthError =
    editPassword.length > 0
      ? validatePasswordStrength(editPassword, passwordMessages)
      : "";
  const editPasswordMismatch =
    (editPassword.length > 0 || editPasswordConfirm.length > 0) &&
    editPassword !== editPasswordConfirm;
  const isEditDisabled =
    saving || editPasswordStrengthError.length > 0 || editPasswordMismatch;

  const rows = users.map((user) => ({
    user,
    email: user.email,
    name: user.name ?? "",
    role: roleLabels(user.roles, t),
    eans: user.eans.join(", "),
    apiUsers: user.apiUsers.join(", "),
  }));
  const sortedRows = sortConfig.key
    ? getSortedBoard(rows, sortConfig.key, sortConfig.direction)
    : rows;

  return (
    <>
      <Panel>
        <OperationArea
          title={t.adminUsersScreen.title}
          toolbarButton={{
            show: true,
            text: t.adminUsersScreen.create.button,
            onClick: openCreate,
          }}
        >
          <Board id="table-admin-users">
            <BoardHead>
              <BoardTr>
                <BoardTh
                  text={t.adminUsersScreen.table.thead.email}
                  onClick={() => sortData("email")}
                />
                <BoardTh
                  text={t.adminUsersScreen.table.thead.name}
                  onClick={() => sortData("name")}
                />
                <BoardTh
                  text={t.adminUsersScreen.table.thead.role}
                  onClick={() => sortData("role")}
                />
                <BoardTh
                  text={t.adminUsersScreen.table.thead.eans}
                  onClick={() => sortData("eans")}
                />
                <BoardTh
                  text={t.adminUsersScreen.table.thead.apiUsers}
                  onClick={() => sortData("apiUsers")}
                />
                <BoardTh text={t.adminUsersScreen.table.thead.actions} />
              </BoardTr>
            </BoardHead>

            <BoardBody isLoading={loading}>
              {sortedRows.map(({ user, name, role, eans, apiUsers }) => (
                <BoardTr key={`admin-user-${user.id}`}>
                  <BoardTd>
                    <p className="board-bold">{user.email}</p>
                  </BoardTd>
                  <BoardTd>
                    <p>{name}</p>
                  </BoardTd>
                  <BoardTd>
                    <p>{role}</p>
                  </BoardTd>
                  <BoardTd>
                    <p>{eans}</p>
                  </BoardTd>
                  <BoardTd>
                    <p>{apiUsers}</p>
                  </BoardTd>
                  <BoardTd hasClassNameDirection={false}>
                    <IconButton
                      icon={PencilOutline}
                      alt={t.adminUsersScreen.edit.editButton}
                      onClick={() => openEdit(user)}
                    />
                    <IconButton
                      icon={TrashOutline}
                      alt={t.adminUsersScreen.delete.deleteButton}
                      variant="danger"
                      disabled={user.email === currentUserEmail}
                      onClick={() => setDeletingUser(user)}
                    />
                  </BoardTd>
                </BoardTr>
              ))}
            </BoardBody>
          </Board>
        </OperationArea>

        {/* EDITAR USUARIO */}
        <Modal
          open={editing !== null}
          setOpen={(open) => !open && setEditing(null)}
          title={t.adminUsersScreen.edit.title}
          actions={[
            {
              text: t.adminUsersScreen.edit.save,
              onClick: handleSave,
              disabled: isEditDisabled,
            },
          ]}
        >
          <Edit orientation="vertical">
            <Edit>
              <div className="users-field-column">
                <Edit.Divider text={t.adminUsersScreen.table.thead.email} />
                <Edit.Input id="email" value={editing?.email ?? ""} disabled />
              </div>
              <div className="users-field-column">
                <Edit.Divider text={t.adminUsersScreen.edit.nameLabel} />
                <Edit.Input
                  id="name"
                  placeholder={t.adminUsersScreen.edit.namePlaceholder}
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                />
              </div>
            </Edit>

            <Edit.Divider text={t.adminUsersScreen.edit.eansLabel} />
            <EanLinksEditor
              rows={editForm.eanLinks}
              onChange={(eanLinks) => setEditForm({ ...editForm, eanLinks })}
            />

            <Edit.Divider text={t.adminUsersScreen.edit.apiUsersLabel} />
            <Edit.Input
              id="apiUsers"
              placeholder={t.adminUsersScreen.edit.apiUsersPlaceholder}
              value={editForm.apiUsers}
              onChange={(e) =>
                setEditForm({ ...editForm, apiUsers: e.target.value })
              }
            />

            <Edit>
              <div className="users-field-column">
                <Edit.Divider text={t.adminUsersScreen.edit.passwordLabel} />
                <Edit.Password
                  id="password"
                  placeholder={t.adminUsersScreen.edit.passwordHint}
                  value={editPassword}
                  onChange={(e) => setEditPassword(e.target.value)}
                  onBlur={() => setEditPasswordTouched(true)}
                  error={
                    editPasswordTouched ? editPasswordStrengthError : ""
                  }
                />
              </div>
              <div className="users-field-column">
                <Edit.Divider
                  text={t.adminUsersScreen.edit.passwordConfirmLabel}
                />
                <Edit.Password
                  id="passwordConfirm"
                  placeholder={t.adminUsersScreen.edit.passwordConfirmHint}
                  value={editPasswordConfirm}
                  onChange={(e) => setEditPasswordConfirm(e.target.value)}
                  error={
                    editPasswordMismatch
                      ? t.global.password.errors.mismatch
                      : ""
                  }
                />
              </div>
            </Edit>

            <Toggle
              checked={editForm.isAdmin}
              onChange={(checked) =>
                setEditForm({ ...editForm, isAdmin: checked })
              }
              text={t.adminUsersScreen.roles.admin}
            />
            {updateError && <p className="users-form-error">{updateError}</p>}
          </Edit>
        </Modal>

        {/* CREAR USUARIO */}
        <Modal
          open={creatingOpen}
          setOpen={setCreatingOpen}
          title={t.adminUsersScreen.create.title}
          actions={[
            {
              text: t.adminUsersScreen.create.save,
              onClick: handleCreate,
              disabled: isCreateDisabled,
            },
          ]}
        >
          <Edit orientation="vertical">
            <Edit>
              <div className="users-field-column">
                <Edit.Divider text={t.adminUsersScreen.create.emailLabel} />
                <Edit.Input
                  id="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>
              <div className="users-field-column">
                <Edit.Divider text={t.adminUsersScreen.edit.nameLabel} />
                <Edit.Input
                  id="name"
                  placeholder={t.adminUsersScreen.edit.namePlaceholder}
                  value={createForm.name}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, name: e.target.value })
                  }
                />
              </div>
            </Edit>

            <Edit.Divider text={t.adminUsersScreen.edit.eansLabel} />
            <EanLinksEditor
              rows={createForm.eanLinks}
              onChange={(eanLinks) =>
                setCreateForm({ ...createForm, eanLinks })
              }
            />

            <Edit.Divider text={t.adminUsersScreen.edit.apiUsersLabel} />
            <Edit.Input
              id="apiUsers"
              placeholder={t.adminUsersScreen.edit.apiUsersPlaceholder}
              value={createForm.apiUsers}
              onChange={(e) =>
                setCreateForm({ ...createForm, apiUsers: e.target.value })
              }
            />

            <Edit>
              <div className="users-field-column">
                <Edit.Divider text={t.adminUsersScreen.create.passwordLabel} />
                <Edit.Password
                  id="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onBlur={() => setNewPasswordTouched(true)}
                  error={newPasswordTouched ? newPasswordStrengthError : ""}
                />
              </div>
              <div className="users-field-column">
                <Edit.Divider
                  text={t.adminUsersScreen.edit.passwordConfirmLabel}
                />
                <Edit.Password
                  id="passwordConfirm"
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  error={
                    newPasswordMismatch && newPasswordConfirm.length > 0
                      ? t.global.password.errors.mismatch
                      : ""
                  }
                />
              </div>
            </Edit>

            <Toggle
              checked={createForm.isAdmin}
              onChange={(checked) =>
                setCreateForm({ ...createForm, isAdmin: checked })
              }
              text={t.adminUsersScreen.roles.admin}
            />
            {createError && <p className="users-form-error">{createError}</p>}
          </Edit>
        </Modal>

        {/* ELIMINAR USUARIO */}
        <Modal
          open={deletingUser !== null}
          setOpen={(open) => !open && setDeletingUser(null)}
          title={t.adminUsersScreen.delete.title}
          actions={[
            {
              text: t.adminUsersScreen.delete.confirm,
              onClick: handleDelete,
              disabled: deleting,
            },
          ]}
        >
          <p>
            {t.adminUsersScreen.delete.description} {deletingUser?.email}?
          </p>
          {deleteError && <p className="users-form-error">{deleteError}</p>}
        </Modal>
      </Panel>
    </>
  );
};

export default UsersScreen;
